import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RoomsPanel from "@/components/RoomsPanel";
import {
  ArrowLeft,
  UsersThree,
  PaperPlaneTilt,
  Trash,
  Copy,
  SignOut,
  Warning,
} from "@phosphor-icons/react";

const ROLES = [
  { value: "owner", label: "Owner", desc: "Full access. Can invite, remove, and delete." },
  { value: "editor", label: "Editor", desc: "Can read and write all records." },
  { value: "viewer", label: "Viewer", desc: "Read-only access. Cannot modify records." },
  { value: "executor", label: "Executor", desc: "Read-only, intended for estate executors." },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));

export default function HouseholdDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshHouseholds, setActiveLedger, activeLedgerId } = useAuth();

  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [inviteRole, setInviteRole] = useState("editor");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Always operate on this household's ledger context for owner/member ops.
  const requireActive = useCallback(() => {
    if (activeLedgerId !== id) {
      setActiveLedger(id);
      // We re-fetch fresh data with new header next render via dependency.
    }
  }, [activeLedgerId, id, setActiveLedger]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Use explicit X-Ledger-Id header so this request works even if user is currently on Personal.
      const r = await api.get(`/households/${id}`, { headers: { "X-Ledger-Id": id } });
      setH(r.data);
      setRenameValue(r.data.name || "");
      setError(null);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not load household.");
      setH(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const isOwner = h?.my_role === "owner";
  const myUserId = user?.user_id;

  const inviteUrl = (token) => `${window.location.origin}/invitations/${token}`;

  const onInvite = async (e) => {
    e.preventDefault();
    requireActive();
    setInviting(true);
    try {
      await api.post(
        `/households/${id}/invitations`,
        { role: inviteRole, email_hint: inviteEmail.trim() || null },
        { headers: { "X-Ledger-Id": id } },
      );
      toast.success("Invitation token created.");
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create invitation.");
    } finally {
      setInviting(false);
    }
  };

  const copyInvite = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      toast.success("Invite link copied.");
    } catch {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  const revokeInvite = async (invId) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try {
      await api.delete(`/households/${id}/invitations/${invId}`, {
        headers: { "X-Ledger-Id": id },
      });
      toast.success("Invitation revoked.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not revoke.");
    }
  };

  const onChangeRole = async (memberUserId, role) => {
    try {
      await api.patch(
        `/households/${id}/members/${memberUserId}`,
        { role },
        { headers: { "X-Ledger-Id": id } },
      );
      toast.success("Role updated.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update role.");
    }
  };

  const onRemoveMember = async (memberUserId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this household?`)) return;
    try {
      await api.delete(`/households/${id}/members/${memberUserId}`, {
        headers: { "X-Ledger-Id": id },
      });
      toast.success("Member removed.");
      load();
      refreshHouseholds();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove member.");
    }
  };

  const onLeave = async () => {
    if (!myUserId) return;
    if (!window.confirm("Leave this household? You will lose access to its records.")) return;
    try {
      await api.delete(`/households/${id}/members/${myUserId}`, {
        headers: { "X-Ledger-Id": id },
      });
      toast.success("You have left the household.");
      if (activeLedgerId === id) setActiveLedger(null);
      await refreshHouseholds();
      navigate("/households");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not leave household.");
    }
  };

  const onRename = async (e) => {
    e.preventDefault();
    if (!renameValue.trim() || renameValue.trim() === h.name) return;
    setRenaming(true);
    try {
      await api.patch(
        `/households/${id}`,
        { name: renameValue.trim() },
        { headers: { "X-Ledger-Id": id } },
      );
      toast.success("Household renamed.");
      load();
      refreshHouseholds();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not rename.");
    } finally {
      setRenaming(false);
    }
  };

  const onDelete = async () => {
    const confirm = window.prompt(
      `This permanently deletes "${h.name}" and ALL its assets, components, maintenance, files, and share links. Type DELETE to confirm.`,
    );
    if (confirm !== "DELETE") return;
    try {
      await api.delete(`/households/${id}`, { headers: { "X-Ledger-Id": id } });
      toast.success("Household deleted.");
      if (activeLedgerId === id) setActiveLedger(null);
      await refreshHouseholds();
      navigate("/households");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete household.");
    }
  };

  const sortedMembers = useMemo(
    () => (h?.members || []).slice().sort((a, b) => (a.joined_at || "").localeCompare(b.joined_at || "")),
    [h],
  );

  if (loading) {
    return (
      <div data-testid="household-detail-loading" className="text-sm text-[#5C5F58]">
        Loading household…
      </div>
    );
  }
  if (error || !h) {
    return (
      <div data-testid="household-detail-error" className="space-y-4">
        <button
          onClick={() => navigate("/households")}
          className="inline-flex items-center gap-2 text-sm text-[#1A3622] hover:underline"
        >
          <ArrowLeft size={14} /> Back to households
        </button>
        <div className="border border-[#E0DDD6] bg-white p-8 text-center">
          <Warning size={32} weight="thin" className="mx-auto text-[#6B1718] mb-3" />
          <p className="font-serif-display text-xl text-[#0A192F]">{error || "Household not found."}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="household-detail-page" className="space-y-8">
      <button
        onClick={() => navigate("/households")}
        data-testid="household-back-btn"
        className="inline-flex items-center gap-2 text-sm text-[#1A3622] hover:underline"
      >
        <ArrowLeft size={14} /> Back to households
      </button>

      <header className="flex items-end justify-between border-b border-[#C9C4B8] pb-6 gap-4">
        <div className="min-w-0">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
            Household · {ROLE_LABEL[h.my_role]} access
          </div>
          <h1 className="font-serif-display text-5xl text-[#0A192F] leading-none truncate" data-testid="household-name">
            {h.name}
          </h1>
          <p className="text-xs font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mt-3">
            Established {fmtDate(h.created_at)}
          </p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {activeLedgerId === id ? (
            <span
              data-testid="household-active-badge"
              className="px-2 py-1 bg-[#1A3622] text-[#F9F8F6] text-[10px] font-mono-ledger uppercase tracking-[0.2em] text-center"
            >
              Active Ledger
            </span>
          ) : (
            <button
              onClick={() => {
                setActiveLedger(id);
                toast.success(`Switched to ${h.name}.`);
              }}
              data-testid="household-set-active"
              className="text-xs border border-[#1A3622] text-[#1A3622] px-3 py-1.5 hover:bg-[#1A3622] hover:text-[#F9F8F6] transition-colors"
            >
              Set as active
            </button>
          )}
          {!isOwner && (
            <button
              onClick={onLeave}
              data-testid="household-leave-btn"
              className="text-xs border border-[#6B1718] text-[#6B1718] px-3 py-1.5 hover:bg-[#6B1718] hover:text-[#F9F8F6] inline-flex items-center justify-center gap-1.5 transition-colors"
            >
              <SignOut size={12} /> Leave
            </button>
          )}
        </div>
      </header>

      {/* Owner-only: rename */}
      {isOwner && (
        <section className="border border-[#E0DDD6] bg-white p-6">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
            Rename Household
          </div>
          <form onSubmit={onRename} className="flex flex-col sm:flex-row gap-3" data-testid="household-rename-form">
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              data-testid="household-rename-input"
              className="flex-1 border border-[#E0DDD6] focus:border-[#1A3622] focus:outline-none px-4 py-2.5 text-sm bg-white"
              maxLength={120}
            />
            <button
              type="submit"
              disabled={renaming || !renameValue.trim() || renameValue.trim() === h.name}
              data-testid="household-rename-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 transition-colors"
            >
              {renaming ? "Saving…" : "Rename"}
            </button>
          </form>
        </section>
      )}

      {/* Members */}
      <section className="space-y-3">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
          Members · {sortedMembers.length}
        </div>
        <ul className="border border-[#E0DDD6] bg-white divide-y divide-[#E0DDD6]" data-testid="household-members-list">
          {sortedMembers.map((m) => {
            const isMe = m.user_id === myUserId;
            const isHouseholdOwner = m.user_id === h.owner_user_id;
            return (
              <li
                key={m.id}
                data-testid={`member-row-${m.user_id}`}
                className="px-5 py-4 flex items-center gap-4"
              >
                <div className="w-9 h-9 bg-[#1A3622] text-[#F9F8F6] flex items-center justify-center font-serif-display shrink-0">
                  {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#0A192F] font-medium truncate">
                    {m.name}
                    {isMe && <span className="text-[#5C5F58] font-normal"> (you)</span>}
                  </div>
                  <div className="text-xs text-[#5C5F58] truncate">{m.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isOwner && !isHouseholdOwner ? (
                    <select
                      value={m.role}
                      onChange={(e) => onChangeRole(m.user_id, e.target.value)}
                      data-testid={`member-role-select-${m.user_id}`}
                      className="border border-[#E0DDD6] focus:border-[#1A3622] focus:outline-none px-2 py-1 text-xs bg-white"
                    >
                      {ROLES.filter((r) => r.value !== "owner").map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                      <option value="owner">Owner</option>
                    </select>
                  ) : (
                    <span
                      data-testid={`member-role-${m.user_id}`}
                      className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58]"
                    >
                      {ROLE_LABEL[m.role] || m.role}
                    </span>
                  )}
                  {isOwner && !isHouseholdOwner && (
                    <button
                      onClick={() => onRemoveMember(m.user_id, m.name || m.email)}
                      data-testid={`member-remove-${m.user_id}`}
                      title="Remove member"
                      className="text-[#6B1718] hover:bg-[#6B1718]/10 p-1.5 transition-colors"
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Rooms — visible to every member; write actions gated by isOwner/canEdit in the panel */}
      <RoomsPanel householdId={id} isOwner={isOwner} canEdit={h?.my_role === "editor"} />

      {/* Owner-only: invite */}
      {isOwner && (
        <section className="border border-[#E0DDD6] bg-white p-6 space-y-4">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
            Invite a Member
          </div>
          <form onSubmit={onInvite} className="grid sm:grid-cols-[1fr_180px_auto] gap-3" data-testid="invite-form">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Email hint (optional, for your records)"
              data-testid="invite-email-input"
              className="border border-[#E0DDD6] focus:border-[#1A3622] focus:outline-none px-4 py-2.5 text-sm bg-white"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              data-testid="invite-role-select"
              className="border border-[#E0DDD6] focus:border-[#1A3622] focus:outline-none px-4 py-2.5 text-sm bg-white"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={inviting}
              data-testid="invite-submit-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 transition-colors"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {inviting ? "Generating…" : "Generate invite"}
            </button>
          </form>
          <p className="text-xs text-[#5C5F58]">
            We generate a one-time invitation link. Copy and share it directly with the recipient. The
            email hint is for your reference only — anyone with the link who is signed in can accept.
          </p>

          {/* Pending invitations */}
          <div className="space-y-2 pt-2">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Pending Invitations · {(h.pending_invitations || []).length}
            </div>
            {(h.pending_invitations || []).length === 0 ? (
              <div className="text-xs text-[#5C5F58]">No outstanding invitations.</div>
            ) : (
              <ul className="border border-[#E0DDD6] divide-y divide-[#E0DDD6]" data-testid="pending-invitations-list">
                {h.pending_invitations.map((inv) => (
                  <li
                    key={inv.id}
                    data-testid={`invitation-row-${inv.id}`}
                    className="px-4 py-3 flex items-center gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#0A192F] truncate">
                        {inv.email_hint || <span className="text-[#5C5F58] italic">No email hint</span>}
                      </div>
                      <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mt-0.5">
                        {ROLE_LABEL[inv.role] || inv.role} · Created {fmtDate(inv.created_at)}
                      </div>
                    </div>
                    <button
                      onClick={() => copyInvite(inv.token)}
                      data-testid={`invite-copy-${inv.id}`}
                      title="Copy invite link"
                      className="inline-flex items-center gap-1 border border-[#E0DDD6] hover:border-[#1A3622] px-3 py-1.5 text-xs text-[#0A192F] transition-colors"
                    >
                      <Copy size={12} /> Copy link
                    </button>
                    <button
                      onClick={() => revokeInvite(inv.id)}
                      data-testid={`invite-revoke-${inv.id}`}
                      title="Revoke invitation"
                      className="text-[#6B1718] hover:bg-[#6B1718]/10 p-1.5 transition-colors"
                    >
                      <Trash size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* Owner-only: danger zone */}
      {isOwner && (
        <section className="border border-[#6B1718]/40 bg-[#6B1718]/5 p-6">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#6B1718] mb-3">
            Danger Zone
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-[#0A192F] font-medium">Delete this household</div>
              <div className="text-xs text-[#5C5F58]">
                Permanently removes the household and all its records. This cannot be undone.
              </div>
            </div>
            <button
              onClick={onDelete}
              data-testid="household-delete-btn"
              className="inline-flex items-center gap-2 border border-[#6B1718] text-[#6B1718] px-4 py-2 text-sm hover:bg-[#6B1718] hover:text-[#F9F8F6] transition-colors"
            >
              <Trash size={14} /> Delete
            </button>
          </div>
        </section>
      )}

      {/* Role glossary */}
      <section className="border border-[#E0DDD6] bg-white p-6">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
          Role Glossary
        </div>
        <ul className="grid sm:grid-cols-2 gap-3 text-sm">
          {ROLES.map((r) => (
            <li key={r.value} className="flex items-start gap-3">
              <UsersThree size={14} className="text-[#1A3622] mt-1 shrink-0" />
              <div>
                <div className="text-[#0A192F] font-medium">{r.label}</div>
                <div className="text-xs text-[#5C5F58]">{r.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
